import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

// core
import {
  Typography,
  Box,
  Table,
  TableCell,
  TableRow,
  CircularProgress,
  Paper,
  TableBody,
  IconButton,
  TableHead,
  TableContainer,
  Tooltip,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Button,
  MenuItem,
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import AssessmentIcon from '@material-ui/icons/Assessment';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PhotoAlbumIcon from '@material-ui/icons/PhotoAlbum';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';

// components
import useStyles from './styles';
// Cambiar endpoint
import { getTaskResponsesTeleaudit } from '../../requests/api/taskResponse';
import { UNAUTHORIZED } from '../../utils/const/http';

import Snackbar from '../../components/Snackbar';
import Pagination from '../../components/Pagination';
// eslint-disable-next-line no-shadow
const PreTeleaudit = () => {
  const styles = useStyles();
  const { t } = useTranslation();

  // table states
  const [page, setPage] = useState(0);
  const [rowsToShow, setRowsToShow] = useState(null);
  const rowsPerPage = 10;
  const [taskResponsesQuantity, setTaskResponsesQuantity] = useState(0);

  const states = [
    { value: '', label: t('teleaudit.states.all') },
    { value: 'Completado', label: t('teleaudit.states.done') },
    { value: 'En Progreso', label: t('teleaudit.states.inProgress') },
    { value: 'Pendiente', label: t('teleaudit.states.pending') },
  ];

  const types = [
    { value: '', label: t('teleaudit.types.all') },
    { value: 'os', label: t('teleaudit.types.os') },
    { value: 'sos', label: t('teleaudit.types.sos') },
    { value: 'sosa', label: t('teleaudit.types.sosArea') },
  ];

  const teleauditFilters = JSON.parse(localStorage.getItem('teleauditFilters'));

  const [filters, setFilters] = useState({
    surveyResponseId: teleauditFilters?.surveyResponseId ?? '',
    surveyTitle: teleauditFilters?.surveyTitle ?? '',
    location: teleauditFilters?.location ?? '',
    email: teleauditFilters?.email ?? '',
    dateStart: teleauditFilters?.dateStart ?? '',
    dateEnd: teleauditFilters?.dateEnd ?? '',
    state: teleauditFilters?.state ?? '',
    type: teleauditFilters?.type ?? '',
  });

  // auxiliar states

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [succ, setSucc] = useState('');

  const tableHeaders = [
    {
      id: 'id',
      label: <b>{t('teleaudit.index.id')}</b>,
    },
    {
      id: 'date',
      label: <b>Fecha</b>,
    },
    {
      id: 'locationName',
      label: <b>{t('teleaudit.index.locationName')}</b>,
    },
    {
      id: 'taskName',
      label: <b>{t('teleaudit.index.taskName')}</b>,
    },
    {
      id: 'category',
      label: <b>{t('teleaudit.index.category')}</b>,
    },
    {
      id: 'userName',
      label: <b>{t('teleaudit.index.userName')}</b>,
    },
    {
      id: 'nTareas',
      label: <b>N° Tareas</b>,
    },
    {
      id: 'status',
      label: <b>{t('teleaudit.index.statusTeleaudit')}</b>,
    },
    {
      id: 'actions',
      label: <b>{t('teleaudit.index.toTeleaudit')}</b>,
    },
  ];

  const requestSearch = async (pageOfTable, limit) => {
    setLoading(true);
    const response = await getTaskResponsesTeleaudit(
      pageOfTable,
      limit,
      filters.surveyResponseId,
      filters.surveyTitle,
      filters.location,
      filters.email,
      filters.dateStart,
      filters.dateEnd,
      filters.state,
      filters.type,
    );
    if (response.success) {
      setRowsToShow(response.data.data.data);
      setTaskResponsesQuantity(response.data.data.total);
    }
    setLoading(false);

    if (response.status === UNAUTHORIZED.status) {
      window.location.href = '/dashboard';
    }
  };

  const handleChangePage = (newPage) => {
    const numberNewPage = Number.parseInt(newPage, 10);
    setPage(numberNewPage);
  };

  const handleClear = () => {
    setFilters({
      surveyResponseId: '',
      surveyTitle: '',
      location: '',
      email: '',
      dateStart: '',
      dateEnd: '',
      state: '',
      type: '',
    });
  };

  const handleChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
    });
  };

  const applyFilters = () => {
    localStorage.setItem('teleauditFilters', JSON.stringify(filters));
    setPage(0);
    requestSearch(0, rowsPerPage);
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 'Completado':
        return (
          <p align="center" className={`${styles.tag} ${styles.tagOpen}`}>
            <b>{status}</b>
          </p>
        );
      case 'En Progreso':
        return (
          <p align="center" className={`${styles.tag} ${styles.tagRejected}`}>
            <b>{status}</b>
          </p>
        );
      default:
        return (
          <p align="center" className={`${styles.tag} ${styles.tagProcessing}`}>
            <b>{status}</b>
          </p>
        );
    }
  };

  useEffect(() => {
    requestSearch(page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const searchBar = () => (
    <div className={styles.filtersContainer}>
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 3,
          alignItems: 'center',
        }}
      >
        <TextField
          className={styles.filterText}
          label={t('teleaudit.filters.surveyResponseId')}
          variant="outlined"
          size="small"
          value={filters.surveyResponseId}
          onChange={(e) => {
            const { value } = e.target;
            if (/^\d*$/.test(value)) {
              handleChange('surveyResponseId', value);
            }
          }}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }}
          fullWidth
        />
        <TextField
          className={styles.filterText}
          label={t('teleaudit.filters.surveyTitle')}
          variant="outlined"
          size="small"
          value={filters.surveyTitle}
          onChange={(e) => handleChange('surveyTitle', e.target.value)}
          fullWidth
        />
        <TextField
          className={styles.filterText}
          label={t('teleaudit.filters.location')}
          variant="outlined"
          size="small"
          value={filters.location}
          onChange={(e) => handleChange('location', e.target.value)}
          fullWidth
        />
        <TextField
          className={styles.filterText}
          label={t('teleaudit.filters.email')}
          variant="outlined"
          size="small"
          value={filters.email}
          onChange={(e) => handleChange('email', e.target.value)}
          fullWidth
        />
        <TextField
          className={styles.filterText}
          label={t('teleaudit.filters.dateStart')}
          type="date"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          size="small"
          value={filters.dateStart}
          onChange={(e) => handleChange('dateStart', e.target.value)}
          fullWidth
        />
        <TextField
          className={styles.filterText}
          label={t('teleaudit.filters.dateEnd')}
          type="date"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          size="small"
          value={filters.dateEnd}
          onChange={(e) => handleChange('dateEnd', e.target.value)}
          fullWidth
        />
        <FormControl
          className={styles.filterText}
          variant="outlined"
          size="small"
          fullWidth
        >
          <InputLabel className={styles.inputLabel} shrink>{t('teleaudit.filters.state')}</InputLabel>
          <Select
            label={t('teleaudit.filters.state')}
            value={filters.state}
            onChange={(e) => handleChange('state', e.target.value)}
            displayEmpty
          >
            {states.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          className={styles.filterText}
          variant="outlined"
          size="small"
          fullWidth
        >
          <InputLabel className={styles.inputLabel} shrink>{t('teleaudit.filters.type')}</InputLabel>
          <Select
            label={t('teleaudit.filters.type')}
            value={filters.type}
            onChange={(e) => handleChange('type', e.target.value)}
            displayEmpty
          >
            {types.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          marginTop: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="medium"
          className={styles.applyFilterButton}
          onClick={() => applyFilters()}
        >
          Aplicar filtros
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          className={styles.cleanFiltersButton}
          onClick={handleClear}
        >
          Limpiar filtros
        </Button>
      </Box>
    </div>
  );

  const isPriceAiPending = (row) => row.total_tareas_price_ai_pending > 0;

  const content = () => {
    if (taskResponsesQuantity === 0) {
      return (
        <TableRow>
          <TableCell colSpan={9} className={styles.users}>
            <h4>{t('qa.noTaskResponses')}</h4>
          </TableCell>
        </TableRow>
      );
    }
    return (
      <>
        {rowsToShow.map((row) => (
          <TableRow hover key={row.id}>
            {row.status && (
              <>
                <TableCell align="center">{row.id}</TableCell>
                <TableCell align="center">{row.fecha}</TableCell>
                <TableCell
                  component="th"
                  scope="row"
                  padding="none"
                  className={styles.locationName}
                  width="20%"
                >
                  {row.locationName ? row.locationName : t('qa.noLocationName')}
                </TableCell>
                <TableCell align="left">
                  {row.surveyTitle ? row.surveyTitle : t('qa.noTaskName')}
                </TableCell>
                <TableCell align="left">
                  {row.categoryCode
                    ? `${row.categoryCode} (${row.categoryName})`
                    : 'General'}
                </TableCell>
                <TableCell align="left">
                  {row.userName ? row.userName : t('qa.noUserName')}
                </TableCell>
                <TableCell align="center">{row.tareas_realizadas}/{row.total_tareas}</TableCell>
                <TableCell align="center">
                  {getStatusTag(row.teleaudit_status)}
                </TableCell>
                <TableCell align="center">
                  <Box
                    display='flex'
                    direction='row'
                    style={{ gap: 8 }}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Grid item>
                      <Tooltip title="OS" placement="top" classes={{ tooltip: styles.tooltipContent }}>
                        <span>
                          <IconButton
                            aria-label={t('teleaudit.rowActions.os')}
                            component={Link}
                            to={`/teleaudits/${row.id}/os`}
                            className={styles.iconButton}
                            size="small"
                            disabled={!(row.total_tareas_os > 0)}
                          >
                            <PhotoAlbumIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <Tooltip title="SOS" placement="top" classes={{ tooltip: styles.tooltipContent }}>
                        <span>
                          <IconButton
                            aria-label={t('teleaudit.rowActions.sos')}
                            component={Link}
                            to={`/teleaudits/${row.id}/sos`}
                            className={styles.iconButton}
                            size="small"
                            disabled={!(row.total_tareas_sos > 0)}
                          >
                            <PhotoLibraryIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <Tooltip title="SOV" placement="top" classes={{ tooltip: styles.tooltipContent }}>
                        <span>
                          <IconButton
                            aria-label={t('teleaudit.rowActions.sosArea')}
                            component={Link}
                            to={`/teleaudits/${row.id}/sosarea`}
                            className={styles.iconButton}
                            size="small"
                            disabled={!(row.total_tareas_sosa > 0)}
                          >
                            <AssessmentIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <Tooltip title="TEST" placement="top" classes={{ tooltip: styles.tooltipContent }}>
                        <span>
                          <IconButton
                            aria-label={t('teleaudit.rowActions.survey')}
                            component={Link}
                            to={`/teleaudits/${row.id}/encuesta`}
                            className={styles.iconButton}
                            size="small"
                            disabled={!(row.total_tareas_test > 0)}
                          >
                            <AssignmentIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <Tooltip
                        title={isPriceAiPending(row)
                          ? t('teleaudit.price.tooltipAiPending') : t('teleaudit.price.tooltip')}
                        placement="top"
                        classes={{ tooltip: styles.tooltipContent }}
                      >
                        <span>
                          <IconButton
                            aria-label={t('teleaudit.rowActions.price')}
                            component={Link}
                            to={`/teleaudits/${row.id}/price`}
                            className={clsx(styles.iconButton, {
                              [styles.iconButtonAiPending]: isPriceAiPending(row),
                            })}
                            size="small"
                            disabled={!(row.total_tareas_price > 0) || isPriceAiPending(row)}
                          >
                            <AttachMoneyIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                  </Box>
                </TableCell>
              </>
            )}
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <div className={styles.content}>
      <div className={styles.header}>
        <Box sx={{ width: '100%' }}>
          <Typography variant="h1" className={styles.title} gutterBottom>
            {t('teleaudit.index.subtitle1')}
          </Typography>
          <Typography variant="h4" gutterBottom display="inline">
            {t('teleaudit.index.subtitle2')}
          </Typography>
        </Box>
      </div>
      {searchBar()}
      <TableContainer component={Paper} className={styles.taskResponseTable}>
        <Table
          aria-labelledby="tableTitle"
          size="medium"
          aria-label="enhanced table"
        >
          <TableHead>
            <TableRow>
              {tableHeaders.map((tableHeader) => (
                <TableCell
                  key={tableHeader.id}
                  className={clsx({
                    [styles.tableHeader]: true,
                    [styles.taskNameHeader]: [
                      'locationName',
                      'taskName',
                      'userName',
                      'category',
                    ].includes(tableHeader.id),
                  })}
                  padding="normal"
                  align="left"
                >
                  {tableHeader.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className={styles.progress}>
                  <CircularProgress />
                  <h4>{t('qa.loading')}</h4>
                </TableCell>
              </TableRow>
            ) : (
              content()
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        setPage={setPage}
        handleChangePage={handleChangePage}
        page={page}
        rowsPerPage={rowsPerPage}
        quantity={taskResponsesQuantity}
      />

      <div>
        <Snackbar
          open={err !== ''}
          message={err}
          severity="error"
          onClose={() => setErr('')}
        />
        <Snackbar
          open={succ !== ''}
          message={succ}
          severity="success"
          onClose={() => setSucc('')}
        />
      </div>
    </div>
  );
};

export default PreTeleaudit;
